import { AxiosInstance, AxiosResponse } from "axios"
import { QueryInstance } from "src/types/engine.types"
import { EXPORT_VARIABLE_TYPES, VARIABLE_STYLE } from "types/engineExport.types"

/**
 * Data Reporting API functions - Refactored to use authenticated axios instance
 * Authentication headers are handled automatically by the axios instance
 */

/**
 * Downloads a data map file in CSV format.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The identifier of the data map to be downloaded.
 * @param filename - The desired filename for the downloaded CSV file.
 * @param template - Indicates whether to download as a template.
 * @param groupSections - Indicates whether to group sections in the download.
 * @param variableNames - The type of variable names to download.
 * @param variableStyle - The variable label style (default vs decipher).
 * @param includeQuestionText - When true, adds a QUESTION_TEXT column with full question wording.
 * @returns The response object from the API call.
 * @throws Will throw an error if the download fails.
 */
export async function downloadDatamap(
  axiosInstance: AxiosInstance,
  id: string,
  filename: string,
  template: boolean,
  groupSections: boolean,
  variableNames: EXPORT_VARIABLE_TYPES,
  variableStyle?: VARIABLE_STYLE,
  includeQuestionText?: boolean
): Promise<AxiosResponse<Blob>> {
  const config = {
    params: {} as Record<string, string>, // Initialize query parameters
    responseType: "blob" as const, // This is important to handle the file download
  }

  // Add query parameters to config.params without values
  if (groupSections) {
    config.params.group_sections = "" // or undefined
  }

  if (template) {
    config.params.template = "" // or undefined
  }

  // Only add variableNames if template is not selected
  if (!template && variableNames) {
    config.params.var = variableNames // Will be either 'numbering' or 'labels'
  }

  // Build request body with options carried server-side
  const body: Record<string, unknown> = { id }
  const options: Record<string, unknown> = {}
  if (variableStyle && variableStyle !== VARIABLE_STYLE.DEFAULT) {
    options["label-style"] = variableStyle
  }
  if (includeQuestionText) {
    options["question-text"] = true
  }
  if (Object.keys(options).length > 0) {
    body.options = options
  }

  const response = await axiosInstance.post(
    `/download-datamap`,
    body,
    config
  )

  // Create a URL for the blob
  const url = window.URL.createObjectURL(new Blob([response.data]))
  const link = document.createElement("a")
  link.href = url
  link.setAttribute("download", `${filename}.map.csv`) // Set the filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  return response
}

/**
 * Fetches the data map as a CSV string WITHOUT triggering a browser download.
 *
 * Same endpoint and options as {@link downloadDatamap}, but returns the raw CSV
 * text so it can be parsed and rendered in-app (e.g. the Data File Variables
 * overview). The datamap is generated server-side from the SAVED questionnaire,
 * so no collected data is required.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The project identifier.
 * @param groupSections - When true, group variables by section in the output.
 * @param variableNames - Variable naming method ('numbering' or 'labels').
 * @param variableStyle - The variable label style (default vs decipher).
 * @param includeQuestionText - When true, adds a QUESTION_TEXT column.
 * @param variant - Optional variant name to generate the datamap for.
 * @param template - When true, ask for the TEMPLATE datamap: the VARIABLE column
 *   and the category codes come back empty, ready to be filled in. Mirrors
 *   {@link downloadDatamap}: `template` and `var` are mutually exclusive.
 * @returns The response object whose `data` is the CSV string.
 * @throws Will throw an error if the request fails.
 */
export async function getDatamapCSV(
  axiosInstance: AxiosInstance,
  id: string,
  groupSections: boolean,
  variableNames: EXPORT_VARIABLE_TYPES,
  variableStyle?: VARIABLE_STYLE,
  includeQuestionText?: boolean,
  variant?: string,
  template?: boolean
): Promise<AxiosResponse<string>> {
  const config = {
    params: {} as Record<string, string>,
    responseType: "text" as const,
  }

  if (groupSections) {
    config.params.group_sections = ""
  }

  if (template) {
    config.params.template = ""
  }

  // Only add variableNames if template is not selected
  if (!template && variableNames) {
    config.params.var = variableNames // 'numbering' or 'labels'
  }

  // Build request body with options carried server-side
  const body: Record<string, unknown> = { id }
  const options: Record<string, unknown> = {}
  if (variableStyle && variableStyle !== VARIABLE_STYLE.DEFAULT) {
    options["label-style"] = variableStyle
  }
  if (includeQuestionText) {
    options["question-text"] = true
  }
  if (variant) {
    options.variant = variant
  }
  if (Object.keys(options).length > 0) {
    body.options = options
  }

  const response = await axiosInstance.post<string>(
    `/download-datamap`,
    body,
    config
  )
  return response
}

/**
 * Initializes data reporting for project.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param id - The identifier of the project to initialize data reporting for.
 * @returns The response object from the API call.
 * @throws Will throw an error if the initialization fails.
 */
export async function InitialiseDataReporting(
  axiosInstance: AxiosInstance,
  id: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(`/initialise-data?id=${id}`)
  return response
}

/**
 * Runs a query on a specific engine instance.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param instanceID - The identifier of the query instance to run the query on. Returned from initialise-data.
 * @param query - The query to be executed.
 * @param isAsync - Indicates whether the query should be run asynchronously.
 * @returns The response object from the API call.
 * @throws Will throw an error if the query execution fails.
 */
export async function RunQuery(
  axiosInstance: AxiosInstance,
  instanceID: QueryInstance,
  query: string,
  isAsync: boolean
): Promise<AxiosResponse<unknown>> {
  const config: { headers?: Record<string, number> } = {}

  // Conditionally add the ASync-Request header if isAsync is true
  if (isAsync) {
    config.headers = {
      "ASync-Request": 10,
    }
  }

  const response = await axiosInstance.post(
    `/query-run?${instanceID}`,
    { query: query },
    config
  )

  return response
}

/**
 * TODO - move to a more appropriate location in the codebase as this is not related to data reporting. But only one call currently uses ASync-Request header.
 * Polls the server to check if an asynchronous request is ready.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param asyncID - The identifier for the asynchronous request to check readiness for.
 * @returns The response object from the API call.
 * @throws Will throw an error if polling fails.
 */
export async function pollAsync(
  axiosInstance: AxiosInstance,
  asyncID: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(`/async-ready?id=${asyncID}`)
  return response
}

/**
 * TODO - move to a more appropriate location in the codebase as this is not related to data reporting. But only one call currently uses ASync-Request header.
 * Fetches the result of a completed asynchronous request.
 *
 * @param axiosInstance - Authenticated axios instance
 * @param asyncID - The identifier for the asynchronous request whose result is being fetched.
 * @returns The response object from the API call containing the result data.
 * @throws Will throw an error if fetching the result fails.
 */
export async function fetchAsyncResult(
  axiosInstance: AxiosInstance,
  asyncID: string
): Promise<AxiosResponse<unknown>> {
  const response = await axiosInstance.get(`/async-get?id=${asyncID}`)
  return response
}
